<template>
  <ParticleAnim class="projects-section-wrapper" :class="{ 'theme--light': !$vuetify.theme.dark }">
    <v-container class="projects-section" fluid>
      <!-- Header Section -->
      <section class="section-container enhanced-section">
        <div class="section-header">
          <h2 class="section-title enhanced-title">
            My <span class="gradient-text">Projects</span>
          </h2>
          <div class="section-divider"></div>
          <p class="section-description">
            A collection of my work showcasing different aspects of web development, from frontend interfaces to full-stack applications. Each project represents a unique challenge and learning opportunity.
          </p>
        </div>
      </section>

      <!-- Statistics Section -->
      <section class="stats-section enhanced-section">
        <v-card class="stats-card enhanced-card" elevation="0" :class="{ 'theme--dark': $vuetify.theme.dark }">
          <v-card-text class="stats-content">
            <div class="stats-grid">
              <div
                v-for="stat in projectStats"
                :key="stat.label"
                class="stat-item enhanced-stat-item"
              >
                <v-avatar size="60" :color="$vuetify.theme.dark ? stat.color + ' darken-3' : stat.color + ' lighten-4'" class="stat-icon">
                  <v-icon :color="$vuetify.theme.dark ? stat.color + ' lighten-3' : stat.color" size="32">{{ stat.icon }}</v-icon>
                </v-avatar>
                <div class="stat-number">{{ stat.value }}</div>
                <div class="stat-label">{{ stat.label }}</div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </section>

      <!-- Filter Section -->
      <section class="filter-section enhanced-section">
        <v-card class="filter-card enhanced-card" elevation="0" :class="{ 'theme--dark': $vuetify.theme.dark }">
          <v-card-text class="filter-content">
            <h3 class="filter-title">Project Categories</h3>
            <v-chip-group
              v-model="selectedCategory"
              active-class="primary--text"
              class="filter-chips"
              mandatory
            >
              <v-chip
                v-for="category in categories"
                :key="category"
                :value="category"
                filter
                outlined
                class="filter-chip enhanced-chip"
                :class="{ 'selected-chip': selectedCategory === category }"
              >
                {{ category }}
              </v-chip>
            </v-chip-group>
          </v-card-text>
        </v-card>
      </section>

      <!-- Projects Grid -->
      <section class="projects-grid enhanced-section">
        <v-row class="projects-row">
          <v-col
            v-for="project in filteredProjects"
            :key="project.id"
            cols="12"
            sm="6"
            lg="4"
            class="project-column"
          >
            <v-card
              class="project-card enhanced-card"
              :class="{
                'clickable': project.link,
                'is-hovered': project.isHovered,
                'theme--dark': $vuetify.theme.dark
              }"
              @click="project.link && openLink(project.link)"
              @mouseenter="handleCardHover(project, true)"
              @mouseleave="handleCardHover(project, false)"
              elevation="0"
            >
              <!-- Project Image -->
              <div class="project-image-container">
                <img
                  :src="project.img"
                  :alt="project.name"
                  class="project-image"
                >

                <!-- Status Badge -->
                <div
                  v-if="project.status"
                  class="status-badge"
                  :class="getStatusClass(project.status)"
                >
                  <v-icon size="12" color="white">{{ getStatusIcon(project.status) }}</v-icon>
                  <span>{{ project.status }}</span>
                </div>

                <!-- Hover Overlay -->
                <div class="project-overlay">
                  <div class="overlay-content">
                    <div class="overlay-actions">
                      <v-btn
                        v-if="project.link"
                        icon
                        large
                        color="white"
                        class="overlay-btn"
                        @click.stop="openLink(project.link)"
                      >
                        <v-icon size="24">mdi-open-in-new</v-icon>
                      </v-btn>
                      <v-btn
                        v-if="project.github"
                        icon
                        large
                        color="white"
                        class="overlay-btn"
                        @click.stop="openLink(project.github)"
                      >
                        <v-icon size="24">mdi-github</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Card Content -->
              <v-card-text class="project-content">
                <div class="project-header">
                  <h3 class="project-title">{{ project.name }}</h3>
                  <v-chip
                    v-if="project.status"
                    x-small
                    :color="getStatusColor(project.status)"
                    class="status-chip"
                    text-color="white"
                  >
                    {{ project.status }}
                  </v-chip>
                </div>

                <p class="project-description">{{ project.description }}</p>

                <!-- Metrics -->
                <div v-if="project.metrics" class="project-metrics">
                  <div
                    v-for="(metric, metricIndex) in project.metrics.slice(0, 2)"
                    :key="metricIndex"
                    class="metric-item"
                  >
                    <v-icon x-small :color="metric.color" class="metric-icon">
                      {{ metric.icon }}
                    </v-icon>
                    <span class="metric-text">{{ metric.text }}</span>
                  </div>
                </div>

                <!-- Technologies -->
                <div class="project-technologies">
                  <div class="tech-chips">
                    <v-chip
                      v-for="(tech, techIndex) in project.technologies.slice(0, 4)"
                      :key="techIndex"
                      x-small
                      outlined
                      class="tech-chip"
                      :style="getTechChipStyle(techIndex)"
                    >
                      <v-icon left size="10" :color="getTechColor(tech)">
                        {{ getTechIcon(tech) }}
                      </v-icon>
                      {{ tech }}
                    </v-chip>
                    <span v-if="project.technologies.length > 4" class="more-tech">
                      +{{ project.technologies.length - 4 }} more
                    </span>
                  </div>
                </div>
              </v-card-text>

              <!-- Card Actions -->
              <v-card-actions class="project-actions">
                <v-btn
                  v-if="project.link"
                  small
                  color="primary"
                  class="action-btn"
                  @click.stop="openLink(project.link)"
                >
                  <v-icon left size="14">mdi-launch</v-icon>
                  Demo
                </v-btn>

                <v-btn
                  v-if="project.github"
                  small
                  outlined
                  color="primary"
                  class="action-btn"
                  @click.stop="openLink(project.github)"
                >
                  <v-icon left size="14">mdi-github</v-icon>
                  Code
                </v-btn>

                <v-spacer></v-spacer>

                <v-btn icon x-small @click.stop="toggleLike(project.id)">
                  <v-icon :color="project.liked ? 'red' : 'grey'" size="16">
                    {{ project.liked ? 'mdi-heart' : 'mdi-heart-outline' }}
                  </v-icon>
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </section>

      <!-- Load More Button -->
      <div v-if="hasMoreProjects" class="load-more-section enhanced-section" data-aos="fade-up">
        <v-btn
          color="primary"
          outlined
          rounded
          large
          class="load-more-btn enhanced-btn"
          @click="loadMoreProjects"
          :loading="loadingMore"
        >
          <v-icon left size="20">mdi-plus-circle</v-icon>
          Load More Projects
        </v-btn>
      </div>

      <!-- Call to Action Section -->
      <section class="cta-section enhanced-section" data-aos="fade-up" data-aos-delay="200">
        <v-card class="cta-card enhanced-card" elevation="0" :class="{ 'theme--dark': $vuetify.theme.dark }">
          <v-card-text class="cta-content">
            <div class="cta-icon-container">
              <v-avatar size="80" :color="$vuetify.theme.dark ? 'primary darken-3' : 'primary lighten-4'" class="cta-icon">
                <v-icon :color="$vuetify.theme.dark ? 'primary lighten-3' : 'primary'" size="40">mdi-rocket-launch</v-icon>
              </v-avatar>
            </div>
            <h3 class="cta-title">Ready to Start Your Project?</h3>
            <p class="cta-subtitle">
              Let's collaborate and build something amazing together.
              I'm always excited to take on new challenges and create innovative solutions.
            </p>
            <v-btn
              color="primary"
              rounded
              large
              class="cta-btn enhanced-btn"
              @click="$emit('show-contact')"
            >
              <v-icon left>mdi-email-outline</v-icon>
              Get In Touch
            </v-btn>
          </v-card-text>
        </v-card>
      </section>

      <!-- Spacer for footer -->
      <div class="footer-spacer"></div>
    </v-container>
  </ParticleAnim>
</template>

<script>
import ParticleAnim from '@/components/ParticleAnim.vue';

export default {
  name: "ProjectsComponent",
  components: {
    ParticleAnim
  },
  data() {
    return {
      selectedCategory: 'All',
      loadingMore: false,
      projectsPerPage: 6,
      currentPage: 1,
      colors: ['#4361ee', '#f72585', '#4cc9f0', '#7209b7', '#f77f00', '#06ffa5'],

      categories: [
        'All',
        'Web Application',
        'Mobile App',
        'E-commerce',
        'Blockchain',
        'API/Backend',
        'Tools & Utilities'
      ],

      projectStats: [
        { icon: 'mdi-code-tags', value: '6+', label: 'Live Projects', color: 'primary' },
        { icon: 'mdi-language-javascript', value: '15+', label: 'Technologies', color: 'success' },
        { icon: 'mdi-account-group', value: '1500+', label: 'Active Users', color: 'info' },
        { icon: 'mdi-star', value: '99%', label: 'Uptime', color: 'warning' }
      ],

      projects: [
        {
          id: 1,
          name: "Telegram Bot Integration",
          description: "Created a Telegram bot that automates notifications and user interactions for streamlined communication.",
          shortDescription: "AI-powered Telegram bot for automated communication",
          img: require('@/assets/projects/tgbot.jpg'),
          technologies: ['Python', 'Telegram API', 'MongoDB', 'Redis', 'Docker'],
          status: 'Live',
          category: 'API/Backend',
          link: 'https://t.me/facecheckV1_bot',
          github: '',
          liked: false,
          isHovered: false,
          metrics: [
            { icon: 'mdi-account-group', color: '#4caf50', text: '1000+ active users' },
            { icon: 'mdi-message-processing', color: '#2196f3', text: '10K+ messages processed daily' }
          ]
        },
        {
          id: 2,
          name: "MLMC Investment Platform",
          description: "Developed an investment platform that enables users to track their portfolios and manage investments with real-time data.",
          shortDescription: "Modern investment tracking and portfolio management platform",
          img: require('@/assets/projects/inves.png'),
          technologies: ['Vue.js', 'Vuetify', 'Chart.js', 'Firebase', 'Node.js'],
          status: 'Live',
          category: 'Web Application',
          link: 'https://mlmc-vip.com',
          github: '',
          liked: false,
          isHovered: false,
          metrics: [
            { icon: 'mdi-chart-line', color: '#4caf50', text: '$2M+ assets tracked' },
            { icon: 'mdi-account-multiple', color: '#2196f3', text: '500+ investors' }
          ]
        },
        {
          id: 3,
          name: "Coin App - Crypto Exchange",
          description: "Built a cryptocurrency exchange platform with features like secure trading, wallet management, and real-time market analysis.",
          shortDescription: "Secure cryptocurrency exchange with advanced trading features",
          img: require('@/assets/projects/coinapp.jpg'),
          technologies: ['Vue.js', 'Node.js', 'Express', 'MongoDB', 'WebSocket'],
          status: 'In Development',
          category: 'Blockchain',
          link: '',
          github: '',
          liked: false,
          isHovered: false,
          metrics: [
            { icon: 'mdi-security', color: '#4caf50', text: 'Bank-level security' },
            { icon: 'mdi-currency-btc', color: '#ff9800', text: '50+ cryptocurrencies' }
          ]
        },
        {
          id: 4,
          name: "E-Commerce Platform",
          description: "A comprehensive e-commerce solution with modern UI, payment integration, and inventory management.",
          shortDescription: "Full-featured e-commerce platform with modern design",
          img: require('@/assets/projects/ecommerce.jpg'),
          technologies: ['Vue.js', 'Node.js', 'MongoDB', 'Stripe', 'JWT'],
          status: 'Live',
          category: 'E-commerce',
          link: '',
          github: '',
          liked: false,
          isHovered: false,
          metrics: [
            { icon: 'mdi-cart', color: '#4caf50', text: '1000+ orders processed' },
            { icon: 'mdi-credit-card', color: '#2196f3', text: 'Secure payments' }
          ]
        },
        {
          id: 5,
          name: "Expense Tracker",
          description: "Personal finance management application with budget tracking, expense categorization, and financial goal setting.",
          shortDescription: "Smart expense tracking with budgeting tools",
          img: require('@/assets/projects/expense-tracker.png'),
          technologies: ['Vue.js', 'Chart.js', 'LocalStorage', 'PWA'],
          status: 'Live',
          category: 'Tools & Utilities',
          link: '',
          github: '',
          liked: false,
          isHovered: false,
          metrics: [
            { icon: 'mdi-wallet', color: '#4caf50', text: 'Budget tracking' },
            { icon: 'mdi-chart-pie', color: '#ff9800', text: 'Visual analytics' }
          ]
        },
        {
          id: 6,
          name: "ProTech Solutions Website",
          description: "Corporate website for technology consulting firm with service portfolio, team showcase, and client testimonials.",
          shortDescription: "Professional corporate website for tech consulting",
          img: require('@/assets/projects/protech.png'),
          technologies: ['Vue.js', 'Vuetify', 'Node.js', 'MongoDB'],
          status: 'Live',
          category: 'Web Application',
          link: '',
          github: '',
          liked: false,
          isHovered: false,
          metrics: [
            { icon: 'mdi-domain', color: '#2196f3', text: 'Professional design' },
            { icon: 'mdi-account-tie', color: '#4caf50', text: 'Team showcase' }
          ]
        }
      ]
    };
  },
  computed: {
    filteredProjects() {
      if (this.selectedCategory === 'All') {
        return this.projects.slice(0, this.currentPage * this.projectsPerPage);
      }
      return this.projects
        .filter(project => project.category === this.selectedCategory)
        .slice(0, this.currentPage * this.projectsPerPage);
    },
    hasMoreProjects() {
      const totalProjects = this.selectedCategory === 'All'
        ? this.projects.length
        : this.projects.filter(project => project.category === this.selectedCategory).length;
      return (this.currentPage * this.projectsPerPage) < totalProjects;
    }
  },
  methods: {
    openLink(url) {
      if (url) {
        window.open(url, "_blank");
      }
    },

    handleCardHover(project, isHovered) {
      this.$set(project, 'isHovered', isHovered);
    },

    toggleLike(projectId) {
      const project = this.projects.find(p => p.id === projectId);
      if (project) {
        project.liked = !project.liked;
      }
    },

    loadMoreProjects() {
      this.loadingMore = true;
      setTimeout(() => {
        this.currentPage++;
        this.loadingMore = false;
      }, 1000);
    },

    getStatusColor(status) {
      switch (status.toLowerCase()) {
        case 'live':
          return 'success';
        case 'in development':
          return 'warning';
        case 'planned':
          return 'info';
        default:
          return 'grey';
      }
    },

    getStatusClass(status) {
      switch (status.toLowerCase()) {
        case 'live':
          return 'status-live';
        case 'in development':
          return 'status-development';
        case 'planned':
          return 'status-planned';
        default:
          return 'status-default';
      }
    },

    getStatusIcon(status) {
      switch (status.toLowerCase()) {
        case 'live':
          return 'mdi-check-circle';
        case 'in development':
          return 'mdi-cog';
        case 'planned':
          return 'mdi-clock-outline';
        default:
          return 'mdi-help-circle';
      }
    },

    getTechColor(tech) {
      const techColors = {
        'Vue.js': '#42B883',
        'React': '#61DAFB',
        'Angular': '#E23237',
        'JavaScript': '#F0DB4F',
        'TypeScript': '#007ACC',
        'Python': '#3572A5',
        'Node.js': '#68A063',
        'Express': '#000000',
        'MongoDB': '#47A248',
        'PostgreSQL': '#336791',
        'Firebase': '#FFCA28',
        'Docker': '#2496ED',
        'AWS': '#232F3E',
        'Git': '#F05032'
      };
      return techColors[tech] || '#666';
    },

    getTechIcon(tech) {
      const techIcons = {
        'Vue.js': 'mdi-vuejs',
        'React': 'mdi-react',
        'Angular': 'mdi-angular',
        'JavaScript': 'mdi-language-javascript',
        'TypeScript': 'mdi-language-typescript',
        'Python': 'mdi-language-python',
        'Node.js': 'mdi-nodejs',
        'Express': 'mdi-server',
        'MongoDB': 'mdi-database',
        'PostgreSQL': 'mdi-database',
        'Firebase': 'mdi-firebase',
        'Docker': 'mdi-docker',
        'AWS': 'mdi-amazon-web-services',
        'Git': 'mdi-git'
      };
      return techIcons[tech] || 'mdi-code-tags';
    },

    getTechChipStyle(index) {
      return {
        borderColor: this.colors[index % this.colors.length],
        color: this.colors[index % this.colors.length]
      };
    }
  },
  mounted() {
    // Initialize AOS
    if (window.AOS) {
      window.AOS.refresh();
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/styles/variables.scss";

/* Theme-aware background wrapper */
.projects-section-wrapper {
  position: relative;
  overflow: hidden;
  min-height: 100vh;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      135deg,
      #0a0a0a 0%,
      #1a1a2e 50%,
      #16213e 100%
    );
    z-index: 1;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(67, 97, 238, 0.05), rgba(72, 149, 239, 0.05));
    z-index: 1;
  }

  /* Light theme styling */
  &.theme--light {
    &::before {
      background: linear-gradient(
        135deg,
        #f8f9fa 0%,
        #e9ecef 50%,
        #dee2e6 100%
      );
    }

    &::after {
      background: linear-gradient(135deg, rgba(67, 97, 238, 0.03), rgba(72, 149, 239, 0.03));
    }
  }

  .v-container {
    position: relative;
    z-index: 2;
  }
}

.projects-section {
  padding: 2rem 1rem;
  position: relative;

  @media (min-width: $breakpoint-sm) {
    padding: 3rem 2rem;
  }
}

/* Section Styling */
.enhanced-section {
  margin-bottom: 4rem;
}

.section-header {
  text-align: center;
  margin-bottom: 3rem;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #ffffff;
  font-family: 'Poppins', sans-serif;

  .theme--light & {
    color: rgba(33, 37, 41, 0.9);
  }

  .gradient-text {
    background: linear-gradient(135deg, $primary, $accent);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  @media (max-width: $breakpoint-xs) {
    font-size: 2rem;
  }
}

.section-divider {
  height: 4px;
  width: 80px;
  background-color: var(--v-primary-base);
  margin: 0 auto 1.5rem;
  border-radius: 2px;
}

.section-description {
  max-width: 800px;
  margin: 0 auto;
  font-size: 1.1rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);

  .theme--light & {
    color: rgba(73, 80, 87, 0.8);
  }

  @media (max-width: $breakpoint-xs) {
    font-size: 1rem;
  }
}

/* Enhanced Cards */
.enhanced-card {
  border-radius: 16px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.05) !important;
  backdrop-filter: blur(15px) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  transition: all 0.3s ease;

  .theme--light & {
    background: rgba(255, 255, 255, 0.8) !important;
    border-color: rgba(67, 97, 238, 0.15) !important;
  }

  &.theme--dark {
    background: rgba(255, 255, 255, 0.05) !important;
    border: 1px solid rgba(255, 255, 255, 0.1) !important;
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  }
}

/* Statistics Section */
.stats-content {
  padding: 3rem 2rem;
  text-align: center;
}

.stats-grid {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 3rem;

  @media (max-width: $breakpoint-sm) {
    gap: 2rem;
  }
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
}

.stat-number {
  font-size: 2rem;
  font-weight: 700;
  color: var(--v-primary-base);

  .theme--dark & {
    color: white;
  }
}

.stat-label {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;

  .theme--light & {
    color: rgba(73, 80, 87, 0.7);
  }
}

/* Filter Section */
.filter-content {
  padding: 2rem;
  text-align: center;
}

.filter-title {
  font-size: 1.4rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: white;

  .theme--light & {
    color: rgba(33, 37, 41, 0.9);
  }
}

.filter-chips {
  justify-content: center;
}

.filter-chip {
  margin: 0.25rem;
  transition: all 0.3s ease;
  border-color: rgba(255, 255, 255, 0.3) !important;

  .theme--light & {
    border-color: rgba(67, 97, 238, 0.3) !important;
  }

  &:hover, &.selected-chip {
    transform: translateY(-2px);
    border-color: var(--v-primary-base) !important;
  }
}

/* Projects Grid */
.projects-row {
  margin: 0 -0.5rem;
}

.project-column {
  padding: 0.5rem;
}

.project-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  position: relative;
  overflow: hidden;

  &.is-hovered {
    transform: translateY(-8px);
    box-shadow: 0 25px 50px rgba(67, 97, 238, 0.15);

    .project-overlay {
      opacity: 1;
    }
  }
}

/* Project Image */
.project-image-container {
  position: relative;
  height: 200px;
  overflow: hidden;
}

.project-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;

  .project-card:hover & {
    transform: scale(1.1);
  }
}

.status-badge {
  position: absolute;
  top: 1rem;
  right: 1rem;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  z-index: 2;

  &.status-live {
    background: rgba(76, 175, 80, 0.9);
    color: white;
  }

  &.status-development {
    background: rgba(255, 152, 0, 0.9);
    color: white;
  }

  &.status-planned {
    background: rgba(33, 150, 243, 0.9);
    color: white;
  }
}

.project-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  opacity: 0;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.overlay-content {
  text-align: center;
}

.overlay-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.overlay-btn {
  background: rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(255, 255, 255, 0.3) !important;
    transform: scale(1.1);
  }
}

/* Project Content */
.project-content {
  flex: 1;
  padding: 1.5rem;
}

.project-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.project-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: white;
  margin: 0;
  flex: 1;

  .theme--light & {
    color: rgba(33, 37, 41, 0.9);
  }
}

.status-chip {
  margin-left: 0.5rem;
  flex-shrink: 0;
}

.project-description {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  line-height: 1.5;
  margin-bottom: 1rem;

  .theme--light & {
    color: rgba(73, 80, 87, 0.8);
  }
}

.project-metrics {
  margin-bottom: 1rem;
}

.metric-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.8rem;
}

.metric-text {
  color: rgba(255, 255, 255, 0.7);

  .theme--light & {
    color: rgba(73, 80, 87, 0.7);
  }
}

.project-technologies {
  margin-bottom: 1rem;
}

.tech-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  align-items: center;
}

.tech-chip {
  font-size: 0.7rem;
  height: 24px;

  .v-icon {
    margin-right: 0.25rem !important;
  }
}

.more-tech {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.6);
  font-style: italic;

  .theme--light & {
    color: rgba(73, 80, 87, 0.6);
  }
}

/* Project Actions */
.project-actions {
  padding: 0 1.5rem 1.5rem;
}

.action-btn {
  text-transform: none;
  font-weight: 500;
  margin-right: 0.5rem;

  &:hover {
    transform: translateY(-2px);
  }
}

/* Load More Section */
.load-more-section {
  text-align: center;
  margin: 3rem 0;
}

.load-more-btn {
  min-width: 200px;
  height: 50px;
  font-weight: 600;
  text-transform: none;

  &:hover {
    transform: translateY(-3px);
  }
}

/* CTA Section */
.cta-content {
  padding: 3rem 2rem;
  text-align: center;
}

.cta-icon-container {
  margin-bottom: 2rem;
  display: flex;
  justify-content: center;
}

.cta-title {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: white;

  .theme--light & {
    color: rgba(33, 37, 41, 0.9);
  }
}

.cta-subtitle {
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  color: rgba(255, 255, 255, 0.8);

  .theme--light & {
    color: rgba(73, 80, 87, 0.8);
  }
}

.cta-btn {
  min-width: 200px;
  height: 50px;
  font-weight: 600;
  text-transform: none;

  &:hover {
    transform: translateY(-3px);
  }
}

/* Enhanced Button Styles */
.enhanced-btn {
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 8px 25px rgba(67, 97, 238, 0.3);
  }
}

/* Footer Spacer */
.footer-spacer {
  height: 4rem;
}

/* Responsive Design */
@media (max-width: $breakpoint-xs) {
  .stats-grid {
    flex-direction: column;
    gap: 1.5rem;
  }

  .filter-chips {
    flex-direction: column;
  }

  .project-header {
    flex-direction: column;
    gap: 0.5rem;
  }

  .cta-title {
    font-size: 1.5rem;
  }
}

/* Animation Keyframes */
@keyframes expandWidth {
  from {
    width: 0;
  }
  to {
    width: 60px;
  }
}
</style>